import { logger } from './logger'

const BASE_URL = 'https://api.dropboxapi.com/2'
const CONTENT_BASE_URL = 'https://content.dropboxapi.com/2'
const APP_KEY = import.meta.env.VITE_DROPBOX_APP_KEY

// Note: In production, access tokens should be managed server-side
// This is a client-side implementation for demonstration
let accessToken = null

export const dropboxClient = {
  setAccessToken(token) {
    accessToken = token
  },

  getAccessToken() {
    return accessToken
  },

  async request(method, path, body = null, useContentApi = false) {
    const startTime = Date.now()
    const baseUrl = useContentApi ? CONTENT_BASE_URL : BASE_URL
    const url = `${baseUrl}${path}`
    
    // Prepare headers
    const headers = {
      'Authorization': `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    }
    
    // For file operations, use different content type
    if (useContentApi && method === 'POST') {
      headers['Content-Type'] = 'application/octet-stream'
      if (body && typeof body === 'object' && !body.data) {
        headers['Dropbox-API-Arg'] = JSON.stringify(body)
        body = null // File data should be in body for upload
      }
    }
    
    // Log the request
    const logEntry = logger.logRequest('dropbox', method, url, {
      headers: { ...headers, Authorization: 'Bearer [REDACTED]' },
      body: body && typeof body === 'object' ? JSON.stringify(body) : body
    })
    
    try {
      if (!accessToken) {
        throw new Error('No access token available. Please authenticate with Dropbox first.')
      }
      
      const requestOptions = {
        method,
        headers,
      }
      
      if (body) {
        if (typeof body === 'string' || body instanceof ArrayBuffer) {
          requestOptions.body = body
        } else {
          requestOptions.body = JSON.stringify(body)
        }
      }
      
      const response = await fetch(url, requestOptions)
      const duration = Date.now() - startTime
      
      let responseData
      const contentType = response.headers.get('content-type')
      
      if (contentType && contentType.includes('application/json')) {
        responseData = await response.json()
      } else {
        responseData = await response.text()
      }
      
      // Log the response
      logger.logResponse(logEntry.id, response.status, responseData, duration, {
        'content-type': contentType,
        'status': response.status,
        'statusText': response.statusText
      })
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`)
      }
      
      return responseData
      
    } catch (error) {
      const duration = Date.now() - startTime
      logger.logError(logEntry.id, error, 0)
      throw error
    }
  },

  // Authentication methods (for demonstration - should be server-side)
  async refreshAccessToken(refreshToken) {
    const logEntry = logger.logRequest('dropbox', 'POST', 'https://api.dropbox.com/oauth2/token', {
      grant_type: 'refresh_token',
      refresh_token: '[REDACTED]'
    })
    
    try {
      const response = await fetch('https://api.dropbox.com/oauth2/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
          client_id: APP_KEY,
          // client_secret should come from server-side
        })
      })
      
      const data = await response.json()
      logger.logResponse(logEntry.id, response.status, { access_token: '[REDACTED]', ...data }, Date.now())
      
      if (response.ok && data.access_token) {
        this.setAccessToken(data.access_token)
        return data.access_token
      } else {
        throw new Error('Failed to refresh access token')
      }
    } catch (error) {
      logger.logError(logEntry.id, error)
      throw error
    }
  },

  // File operations
  async listFiles(path = '', recursive = false) {
    return this.request('POST', '/files/list_folder', {
      path,
      recursive,
      include_media_info: false,
      include_deleted: false,
      include_has_explicit_shared_members: false
    })
  },

  async uploadFile(path, fileContent, mode = 'add') {
    return this.request('POST', '/files/upload', {
      path,
      mode,
      autorename: true
    }, true)
  },

  async downloadFile(path) {
    return this.request('POST', '/files/download', { path }, true)
  },

  async getFileMetadata(path) {
    return this.request('POST', '/files/get_metadata', { path })
  },

  async deleteFile(path) {
    return this.request('POST', '/files/delete_v2', { path })
  },

  // Account information
  async getCurrentAccount() {
    return this.request('POST', '/users/get_current_account')
  },

  async getSpaceUsage() {
    return this.request('POST', '/users/get_space_usage')
  },

  // Generate example requests for testing
  getExampleRequests() {
    return [
      {
        name: 'Get Current Account',
        method: 'POST',
        path: '/users/get_current_account',
        body: null
      },
      {
        name: 'List Root Folder',
        method: 'POST',
        path: '/files/list_folder',
        body: {
          path: '',
          recursive: false
        }
      },
      {
        name: 'Get Space Usage',
        method: 'POST',
        path: '/users/get_space_usage',
        body: null
      },
      {
        name: 'Get File Metadata',
        method: 'POST',
        path: '/files/get_metadata',
        body: {
          path: '/example-file.txt'
        }
      },
      {
        name: 'Upload File (Demo)',
        method: 'POST',
        path: '/files/upload',
        body: {
          path: '/uploaded-file.txt',
          mode: 'add',
          autorename: true
        },
        note: 'File content should be provided as binary data'
      }
    ]
  }
}

// TODO: Implement server-side refresh token flow
// This should be handled by a Cloudflare Pages Function for security
export const createRefreshTokenHandler = () => {
  return `
// functions/api/dropbox/refresh-token.js
export async function onRequestPost({ request, env }) {
  try {
    const { refreshToken } = await request.json()
    
    const response = await fetch('https://api.dropbox.com/oauth2/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: refreshToken,
        client_id: env.DROPBOX_APP_KEY,
        client_secret: env.DROPBOX_APP_SECRET,
      })
    })
    
    const data = await response.json()
    
    if (response.ok) {
      return new Response(JSON.stringify({
        access_token: data.access_token,
        expires_in: data.expires_in
      }), {
        headers: { 'Content-Type': 'application/json' }
      })
    } else {
      throw new Error('Token refresh failed')
    }
  } catch (error) {
    return new Response(JSON.stringify({ error: error.message }), {
      status: 500,
      headers: { 'Content-Type': 'application/json' }
    })
  }
}
`
}